import { Attachment, AttachmentStatus, AttachmentType, generateUniID } from '@/states/attachment'
import { css } from '@emotion/react'
import { forwardRef, type HTMLAttributes } from 'react'
import LinkAttachmentPicker from './LinkAttachmentPicker'
import PictureAttachmentPicker from './PictureAttachmentPicker'
import VideoAttachmentPicker from './VideoAttachmentPicker'

export interface MixedAttachmentPickerProps extends HTMLAttributes<HTMLDivElement> {
	/**
	 * @default false
	 */
	noPicture?: boolean

	/**
	 * @default false
	 */
	noVideo?: boolean
	/**
	 * @default false
	 */
	noLink?: boolean

	/**
	 * @default false
	 */
	isPicturePickerDisabled?: boolean

	/**
	 * @default false
	 */
	isVideoPickerDisabled?: boolean

	/**
	 * @default false
	 */
	isLinkPickerDisabled?: boolean

	onAdd?: (attachments: Attachment[]) => void
}

export default forwardRef<HTMLDivElement, MixedAttachmentPickerProps>(
	function MixedAttachmentPicker(
		{
			noPicture = false,
			noVideo = false,
			noLink = false,
			isPicturePickerDisabled = false,
			isVideoPickerDisabled = false,
			isLinkPickerDisabled = false,
			onAdd,
			...props
		},
		ref
	) {
		const handlePictureAttachmentPick = (files: File[]) => {
			const attachments = files.map(
				(file) =>
					({
						id: generateUniID(),
						type: AttachmentType.PICTURE,
						file: file,
						status: AttachmentStatus.WAIT_TO_UPLOAD,
					}) as Attachment
			)

			onAdd?.(attachments)
		}

		return (
			<div
				css={css`
					display: flex;
					align-items: center;
					gap: 20px;
				`}
				{...props}
				ref={ref}
			>
				{!noPicture && (
					<PictureAttachmentPicker
						onSelect={handlePictureAttachmentPick}
						disabled={isPicturePickerDisabled}
					/>
				)}
				{!noVideo && (
					<VideoAttachmentPicker
						onSelect={(files) => {
							onAdd?.(
								files.map((file) => ({
									id: generateUniID(),
									type: AttachmentType.VIDEO,
									file: file,
									status: AttachmentStatus.WAIT_TO_UPLOAD,
								}))
							)
						}}
						disabled={isVideoPickerDisabled}
					/>
				)}
				{!noLink && (
					<LinkAttachmentPicker
						disabled={isLinkPickerDisabled}
						onSelect={(linkInfo) => {
							onAdd?.([
								{
									id: generateUniID(),
									type: AttachmentType.LINK,
									linkInfo,
								},
							])
						}}
					/>
				)}
			</div>
		)
	}
)
